'use client';

import { createContext, useContext, useReducer, ReactNode, useMemo, useCallback } from 'react';

type MediaType = 'audio' | 'video' | 'stream';
type MediaEvent = 'play' | 'pause' | 'seek' | 'volume' | 'timeupdate' | 'buffered' | 'ended';

type MediaMetadata = {
  type: MediaType;
  title: string;
  cover: string;
  isLive?: boolean;
};

type BufferedSegment = {
  start: number;
  end: number;
};

type MediaState = {
  isVisible: boolean;
  isPlaying: boolean;
  currentMedia: string | null;
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;
  metadata: MediaMetadata | null;
  bufferedSegments: BufferedSegment[];
  error: string | null;
  nextMediaQueue: { media: string; metadata: MediaMetadata; delaySeconds: number } | null;
};

type MediaAction =
  | { type: 'SHOW_PLAYER'; media: string; metadata: MediaMetadata }
  | { type: 'SET_PLAYING'; isPlaying: boolean }
  | { type: 'SET_TIME'; currentTime: number; duration: number }
  | { type: 'SET_BUFFERED'; buffered: BufferedSegment[] }
  | { type: 'SET_VOLUME'; volume: number }
  | { type: 'TOGGLE_MUTE'; }
  | { type: 'HIDE_PLAYER' }
  | { type: 'QUEUE_NEXT_MEDIA'; media: string; metadata: MediaMetadata; delaySeconds: number }
  | { type: 'CLEAR_NEXT_MEDIA' }
  | { type: 'SET_ERROR'; error: string | null };

export interface MediaContextValue {
  state: MediaState & { progress: number };
  actions: {
    showPlayer: (media: string, metadata: MediaMetadata) => void;
    togglePlayPause: () => void;
    seek: (percentage: number) => void;
    setVolume: (volume: number) => void;
    toggleMute: () => void;
    hidePlayer: () => void;
    updateTime: (currentTime: number, duration: number) => void;
    updateBuffered: (buffered: BufferedSegment[]) => void;
    setPlayingState: (isPlaying: boolean) => void;
    setError: (error: string | null) => void;
    nextMedia: (media: string, metadata: MediaMetadata, delaySeconds: number) => void;
    clearNextMedia: () => void;
  };
}

const initialState: MediaState = {
  isVisible: false,
  isPlaying: false,
  currentMedia: null,
  currentTime: 0,
  duration: 0,
  volume: 100,
  isMuted: false,
  metadata: null,
  bufferedSegments: [],
  error: null,
  nextMediaQueue: null,
};

const mediaReducer = (state: MediaState, action: MediaAction): MediaState => {
  switch (action.type) {
    case 'SHOW_PLAYER':
      return { ...state, isVisible: true, isPlaying: true, currentMedia: action.media, metadata: action.metadata };
    case 'SET_PLAYING':
      return { ...state, isPlaying: action.isPlaying };
    case 'SET_TIME':
      return { ...state, currentTime: action.currentTime, duration: action.duration };
    case 'SET_BUFFERED':
      return { ...state, bufferedSegments: action.buffered };
    case 'SET_VOLUME':
      return { ...state, volume: action.volume };
    case 'TOGGLE_MUTE':
      return { ...state, isMuted: !state.isMuted };
    case 'HIDE_PLAYER':
      return { ...state, isVisible: false, isPlaying: false, metadata: null, currentMedia: null, currentTime: 0, duration: 0, error: null };
    case 'SET_ERROR':
      return { ...state, error: action.error };
    case 'QUEUE_NEXT_MEDIA':
      return { ...state, nextMediaQueue: { media: action.media, metadata: action.metadata, delaySeconds: action.delaySeconds } };
    case 'CLEAR_NEXT_MEDIA':
      return { ...state, nextMediaQueue: null };
    default:
      return state;
  }
};

export const MediaContext = createContext<MediaContextValue | undefined>(undefined);

export const MediaProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(mediaReducer, initialState);

  const progress = useMemo(() => (state.currentTime / state.duration) * 100, [state.currentTime, state.duration]);

  const showPlayer = useCallback((media: string, metadata: MediaMetadata) => {
    dispatch({ type: 'SHOW_PLAYER', media, metadata });
  }, []);

  const togglePlayPause = useCallback(() => {
    dispatch({ type: 'SET_PLAYING', isPlaying: !state.isPlaying });
  }, [state.isPlaying]);

  const seek = useCallback((percentage: number) => {
    const currentTime = (state.duration * percentage) / 100;
    dispatch({ type: 'SET_TIME', currentTime, duration: state.duration });
  }, [state.duration]);

  const setVolume = useCallback((volume: number) => {
    dispatch({ type: 'SET_VOLUME', volume });
  }, []);

  const toggleMute = useCallback(() => {
    dispatch({ type: 'TOGGLE_MUTE' });
  }, []);

  const hidePlayer = useCallback(() => {
    dispatch({ type: 'HIDE_PLAYER' });
  }, []);

  const updateTime = useCallback((currentTime: number, duration: number) => {
    dispatch({ type: 'SET_TIME', currentTime, duration });
  }, []);

  const updateBuffered = useCallback((buffered: BufferedSegment[]) => {
    dispatch({ type: 'SET_BUFFERED', buffered });
  }, []);

  const setPlayingState = useCallback((isPlaying: boolean) => {
    dispatch({ type: 'SET_PLAYING', isPlaying });
  }, []);

  const setError = useCallback((error: string | null) => {
    dispatch({ type: 'SET_ERROR', error });
  }, []);

  const nextMedia = useCallback((media: string, metadata: MediaMetadata, delaySeconds: number) => {
    dispatch({ type: 'QUEUE_NEXT_MEDIA', media, metadata, delaySeconds });

    setTimeout(() => {
      if (state.nextMediaQueue?.media === media) {
        showPlayer(media, metadata);
        dispatch({ type: 'CLEAR_NEXT_MEDIA' });
      }
    }, delaySeconds * 1000);
  }, [showPlayer, state.nextMediaQueue]);

  const clearNextMedia = useCallback(() => {
    dispatch({ type: 'CLEAR_NEXT_MEDIA' });
  }, []);

  const actions = useMemo(() => ({
    showPlayer,
    togglePlayPause,
    seek,
    setVolume,
    toggleMute,
    hidePlayer,
    updateTime,
    updateBuffered,
    setPlayingState,
    setError,
    nextMedia,
    clearNextMedia
  }), [
    showPlayer,
    togglePlayPause,
    seek,
    setVolume,
    toggleMute,
    hidePlayer,
    updateTime,
    updateBuffered,
    setPlayingState,
    setError,
    nextMedia,
    clearNextMedia
  ]);

  const contextValue = useMemo(() => ({ state: { ...state, progress }, actions }), [state, actions]);

  return (
    <MediaContext.Provider value={contextValue}>
      {children}
    </MediaContext.Provider>
  );
};

export const useMedia = (): MediaContextValue => {
  const context = useContext(MediaContext);
  if (!context) throw new Error('useMedia must be used within a MediaProvider');
  return context;
};